import { motion, useScroll, useTransform } from "framer-motion";
import { useRef, useState } from "react";
import { 
  BookOpen01Icon, 
  AlertCircleIcon, 
  Settings01Icon,
  DropletIcon,
  HelpCircleIcon,
  SparklesIcon,
  ZapIcon
} from "hugeicons-react";
import SectionBadge from "../ui/SectionBadge";

const knowledgeItems = [
  {
    id: "ceramic",
    title: "فشل السيراميك الرخيص",
    shortTitle: "وهم الحماية",
    desc: "السيراميك الرخيص هو مجرد طبقة شمعية معززة بمذيبات تختفي بعد غسلتين. في M Four، نستخدم روابط تقاطع كيميائية (Chemical Cross-linking) تندمج مع طلاء سيارتك لتوفير صلابة 9H حقيقية تدوم لسنوات، وليس لأسابيع.",
    icon: <ZapIcon size={32} />,
    tag: "تقنية النانو",
    impact: "عمر افتراضي قصير جداً"
  },
  {
    id: "wash",
    title: "مقصلة الغسيل الآلي",
    shortTitle: "خيوط العنكبوت",
    desc: "الفرشات الآلية، مهما كانت 'ناعمة'، هي مغناطيس للأتربة من السيارات السابقة. كل غسلة هي عملية صنفرة مجهرية تصنع آلاف 'خيوط العنكبوت' (Swirl Marks) التي تطفئ لمعان سيارتك وتخفض قيمتها السوقية فوراً.",
    icon: <Settings01Icon size={32} />,
    tag: "صحة الطلاء",
    impact: "تدمير اللمعان الأصلي"
  },
  {
    id: "clean",
    title: "خدعة اللمعان الداخلي",
    shortTitle: "فخ الزيوت",
    desc: "اللمعان الزيتي في الصالون لا يعني النظافة. الزيوت الرخيصة تجذب الغبار وتسبب تشقق الجلود مع الوقت. العلامة الحقيقية للنظافة هي المظهر المطفي (Matte) والملمس الجاف والمعقم، وهذا هو معيار السيارات الفاخرة العالمي.",
    icon: <DropletIcon size={32} />,
    tag: "تطهير عميق",
    impact: "تلف الجلود والأسطح"
  }
];

function KnowledgeCard({ item, index }: { item: typeof knowledgeItems[0], index: number }) {
  const [isHovered, setIsHovered] = useState(false);
  
  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.9, y: 30 }}
      whileInView={{ opacity: 1, scale: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.7, delay: index * 0.2 }}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      className="relative group h-[400px] md:h-[450px]"
    >
      {/* Non-Rectangular Outer Container */}
      <div 
        className="absolute inset-0 bg-white/5 backdrop-blur-xl border border-white/10 overflow-hidden transition-all duration-700 group-hover:border-primary/50 group-hover:bg-primary/[0.03]"
        style={{
          clipPath: index % 2 === 0 
            ? "polygon(0% 0%, 100% 0%, 100% 85%, 85% 100%, 0% 100%)" 
            : "polygon(15% 0%, 100% 0%, 100% 100%, 0% 100%, 0% 15%)"
        }}
      >
        {/* Animated Background Pulse */}
        <div className="absolute inset-0 bg-gradient-to-br from-primary/10 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-1000" />
        
        {/* Content Layout */}
        <div className="relative z-10 p-8 md:p-12 h-full flex flex-col justify-between" dir="rtl">
           <div>
              <div className="flex items-center justify-between mb-8">
                 <div className="p-4 bg-primary/10 rounded-2xl border border-primary/20 text-primary group-hover:scale-110 group-hover:bg-primary group-hover:text-white transition-all duration-500 shadow-[0_0_20px_rgba(220,10,50,0.2)]">
                    {item.icon}
                 </div>
                 <span className="text-[10px] font-black text-gray-500 uppercase tracking-[0.3em] group-hover:text-primary transition-colors">{item.tag}</span>
              </div>
              
              <h3 className="text-2xl md:text-3xl font-black text-white mb-4 group-hover:text-primary transition-colors">
                {item.title}
              </h3>
              
              <p className="text-gray-400 text-sm md:text-base leading-relaxed font-bold opacity-80 group-hover:opacity-100 transition-opacity h-[120px] overflow-hidden line-clamp-4">
                {item.desc}
              </p>
           </div>

           <div className="border-t border-white/5 pt-6 flex items-center justify-between">
              <div className="flex items-center gap-2">
                 <AlertCircleIcon size={16} className="text-primary animate-pulse" />
                 <span className="text-xs font-black text-red-500/80">{item.impact}</span>
              </div>
              <motion.div 
                animate={{ x: isHovered ? -10 : 0 }}
                className="text-primary font-black text-xs uppercase tracking-widest flex items-center gap-2"
              >
                 التفاصيل <HelpCircleIcon size={14} />
              </motion.div>
           </div>
        </div>

        {/* Technical HUD Overlays on Hover */}
        <div className="absolute top-0 right-0 p-4 opacity-0 group-hover:opacity-100 transition-opacity duration-500">
           <div className="w-24 h-24 border-t border-r border-primary/20" />
        </div>
        <div className="absolute bottom-0 left-0 p-4 opacity-0 group-hover:opacity-100 transition-opacity duration-500">
           <div className="w-24 h-24 border-b border-l border-primary/20" />
        </div>
        
        {/* Scanning Line on Hover */}
        <motion.div 
          animate={{ top: isHovered ? ['0%', '100%', '0%'] : '50%' }}
          transition={{ duration: 3, repeat: Infinity, ease: "linear" }}
          className="absolute inset-x-0 h-px bg-primary/20 shadow-[0_0_15px_rgba(220,10,50,0.5)] z-0 pointer-events-none opacity-0 group-hover:opacity-100"
        />
      </div>

      {/* Outer Floating Element (Abstract Shape) */}
      <div 
        className="absolute -z-10 -top-4 -left-4 w-24 h-24 bg-primary/10 blur-2xl rounded-full opacity-0 group-hover:opacity-100 transition-opacity duration-700"
      />
    </motion.div>
  );
}

export default function KnowledgeSection() {
  const containerRef = useRef<HTMLDivElement>(null);
  
  const { scrollYProgress } = useScroll({
    target: containerRef,
    offset: ["start end", "end start"]
  });

  const backgroundY = useTransform(scrollYProgress, [0, 1], ["0%", "20%"]);

  return (
    <section 
      ref={containerRef}
      className="py-32 md:py-48 relative overflow-hidden bg-[#050000]"
    >
      {/* High-End Background - Deep Red Glows */}
      <motion.div 
        style={{ y: backgroundY }}
        className="absolute inset-0 pointer-events-none z-0"
      >
         <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-full h-full bg-[radial-gradient(circle_at_center,rgba(220,10,50,0.05)_0%,transparent_70%)]" />
         <div className="absolute top-0 left-0 w-full h-px bg-gradient-to-r from-transparent via-primary/40 to-transparent shadow-[0_0_20px_rgba(220,10,50,0.3)]" />
         <div className="absolute bottom-0 left-0 w-full h-px bg-gradient-to-r from-transparent via-primary/40 to-transparent" />
         
         {/* Moving Gradient Orbs - Strictly Red/Crimson */}
         <motion.div 
            animate={{ 
              x: [0, 100, 0],
              y: [0, 50, 0],
              opacity: [0.1, 0.2, 0.1]
            }}
            transition={{ duration: 15, repeat: Infinity }}
            className="absolute top-1/4 right-0 w-[600px] h-[600px] bg-red-900/10 blur-[150px] rounded-full" 
         />
      </motion.div>

      <div className="container mx-auto px-4 relative z-10">
        {/* Header Section */}
        <div className="text-center mb-24 md:mb-32" dir="rtl">
           <motion.div
             initial={{ opacity: 0, y: 20 }}
             whileInView={{ opacity: 1, y: 0 }}
             viewport={{ once: true }}
             className="inline-flex flex-col items-center gap-6"
           >
              <SectionBadge text="ثقافة العناية" icon={BookOpen01Icon} />
              
              <h2 className="text-4xl md:text-7xl font-black text-white mb-6 tracking-tighter">
                تعليم <span className="text-primary italic">لا تسويق</span>
              </h2>
              
              <p className="text-gray-400 text-lg md:text-xl font-bold max-w-2xl leading-relaxed">
                في M Four، نؤمن أن العميل الواعي هو أفضل شركائنا. الحقيقة قد تكون مؤلمة للبعض، لكنها ضرورية لحماية استثمارك.
              </p>
           </motion.div>
        </div>

        {/* Knowledge Bento Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 md:gap-12 max-w-7xl mx-auto">
           {knowledgeItems.map((item, index) => (
             <KnowledgeCard key={item.id} item={item} index={index} />
           ))}
        </div>

        {/* Call to Logic - Not Hype */}
        <motion.div 
          initial={{ opacity: 0, scale: 0.95 }}
          whileInView={{ opacity: 1, scale: 1 }}
          viewport={{ once: true }}
          className="mt-32 p-12 rounded-[50px] bg-white/5 border border-white/10 relative overflow-hidden group max-w-5xl mx-auto text-center"
        >
           <div className="absolute inset-0 bg-primary/5 opacity-0 group-hover:opacity-100 transition-opacity duration-1000" />
           <div className="relative z-10 flex flex-col items-center" dir="rtl">
              <SparklesIcon size={48} className="text-primary mb-8 animate-bounce-slow" />
              <h4 className="text-3xl md:text-5xl font-black text-white mb-6">هل تبحث عن الحقيقة؟</h4>
              <p className="text-gray-400 text-lg font-bold mb-10 max-w-2xl">
                نحن لا نبيع الوهم. نبيع علماً كيميائياً وحرفية يدوية تم اختبارها لسنوات. سيارتك تستحق أن تُعامل كعمل فني.
              </p>
              
              <div className="flex flex-wrap justify-center gap-6">
                 <div className="flex items-center gap-3 px-6 py-3 bg-white/5 rounded-full border border-white/10 text-white text-sm font-black">
                    <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse" />
                    <span>فريق معتمد دولياً</span>
                 </div>
                 <div className="flex items-center gap-3 px-6 py-3 bg-white/5 rounded-full border border-white/10 text-white text-sm font-black">
                    <div className="w-2 h-2 bg-primary rounded-full animate-pulse" />
                    <span>تجهيزات بمستوى المختبرات</span>
                 </div>
              </div>
           </div>
           
           {/* Abstract Geometric Decoration */}
           <div className="absolute top-0 right-0 w-32 h-32 bg-primary/20 blur-3xl -translate-y-1/2 translate-x-1/2" />
           <div className="absolute bottom-0 left-0 w-32 h-32 bg-primary/20 blur-3xl translate-y-1/2 -translate-x-1/2" />
        </motion.div>

        {/* Signature Marking */}
        <div className="mt-48 flex flex-col items-center opacity-10 hover:opacity-50 transition-opacity duration-1000">
           <div className="h-px w-24 bg-primary mb-6" />
           <p className="font-serif italic text-xl text-white">المنطق.. لا السحر</p>
        </div>
      </div>
    </section>
  );
}
